/** Default per-request deadline for API `fetch` calls (Cloud Run cold starts can take 10–30s). */
export const DEFAULT_REQUEST_TIMEOUT_MS = 30_000;

/** Combine caller AbortSignal with a timeout so hung cold-start connections eventually settle. */
export function withRequestTimeout(
  existing: AbortSignal | null | undefined,
  timeoutMs: number = DEFAULT_REQUEST_TIMEOUT_MS,
): AbortSignal {
  const timeoutSignal =
    typeof AbortSignal !== 'undefined' && typeof AbortSignal.timeout === 'function'
      ? AbortSignal.timeout(timeoutMs)
      : createTimeoutSignal(timeoutMs);

  if (!existing) {
    return timeoutSignal;
  }

  if (typeof AbortSignal.any === 'function') {
    return AbortSignal.any([existing, timeoutSignal]);
  }

  return raceAbortSignals(existing, timeoutSignal);
}

function createTimeoutSignal(timeoutMs: number): AbortSignal {
  const controller = new AbortController();
  const timer = setTimeout(() => {
    controller.abort(new DOMException('The operation was aborted due to timeout', 'TimeoutError'));
  }, timeoutMs);
  controller.signal.addEventListener(
    'abort',
    () => {
      clearTimeout(timer);
    },
    { once: true },
  );
  return controller.signal;
}

function raceAbortSignals(a: AbortSignal, b: AbortSignal): AbortSignal {
  const controller = new AbortController();
  const onAbort = () => {
    const reason = a.aborted ? a.reason : b.reason;
    if (!controller.signal.aborted) {
      controller.abort(reason);
    }
  };
  if (a.aborted || b.aborted) {
    onAbort();
    return controller.signal;
  }
  a.addEventListener('abort', onAbort, { once: true });
  b.addEventListener('abort', onAbort, { once: true });
  return controller.signal;
}

export function isAbortOrTimeoutError(error: unknown): boolean {
  if (error == null || typeof error !== 'object') return false;
  const name =
    'name' in error && typeof (error as { name: unknown }).name === 'string' ? (error as { name: string }).name : '';
  const message =
    'message' in error && typeof (error as { message: unknown }).message === 'string'
      ? (error as { message: string }).message
      : '';
  if (name === 'AbortError' || name === 'TimeoutError') return true;
  return /aborted|timeout/i.test(message);
}
